import React from 'react';
import { useParams } from 'react-router-dom';
import { Table } from 'lucide-react';
import { unitsByCategory } from '../utils/units';
import { convertValue } from '../utils/conversions';
import { SEO } from '../components/SEO';

function ConversionTables() {
  const { category } = useParams<{ category: string }>();
  const units = unitsByCategory[category as keyof typeof unitsByCategory] || [];

  // Generate conversion table data
  const baseUnit = units[0]?.value;
  const tableData = units.map(fromUnit => ({
    unit: fromUnit.label,
    conversions: units.map(toUnit => ({
      to: toUnit.label,
      value: convertValue(1, fromUnit.value, toUnit.value, category || '')
    }))
  }));

  return (
    <>
      <SEO 
        title={`${category.charAt(0).toUpperCase() + category.slice(1)} Conversion Tables`}
        description={`Complete conversion tables for ${category} units. Quick reference for all common ${category} conversions.`}
        canonicalUrl={`https://easyunitconverter.com/tables/${category}`}
        keywords={`${category} conversion table, ${category} units, conversion reference`}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <main className="max-w-7xl mx-auto px-4 py-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            {category.charAt(0).toUpperCase() + category.slice(1)} Conversion Tables
          </h1>

          <div className="space-y-8">
            {/* Quick Reference */}
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Quick Reference
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {units.slice(0, 6).map((unit) => (
                  <div key={unit.value} className="p-4 bg-white rounded-lg border border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-2">1 {unit.label} =</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      {units.slice(0, 3).map((toUnit) => (
                        unit.value !== toUnit.value && (
                          <p key={toUnit.value}>
                            {convertValue(1, unit.value, toUnit.value, category || '').toFixed(4)} {toUnit.label}
                          </p>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Complete Conversion Table */}
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Complete Conversion Table
                <span className="block text-sm font-normal text-gray-500 mt-1">
                  Swipe left/right to view more
                </span>
              </h2>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="min-w-[640px] px-4 sm:px-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-3 text-left font-medium text-gray-600">From \ To</th>
                      {units.map((unit) => (
                        <th key={unit.value} className="p-3 text-left font-medium text-gray-600">
                          {unit.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="p-3 font-medium text-gray-900">
                          {row.unit}
                        </td>
                        {row.conversions.map((conv, j) => (
                          <td key={j} className="p-3 text-gray-600">
                            {conv.value.toFixed(4)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </section>

            {/* Common Conversions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Common Conversions
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 10, 100].map((value) => (
                  <div key={value} className="p-4 bg-white rounded-lg border border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-2 break-words">
                      {value} {units[0]?.label}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      {units.slice(1, 4).map((unit) => (
                        <p key={unit.value} className="break-words">
                          = {convertValue(value, units[0]?.value || '', unit.value, category || '').toFixed(4)} {unit.label}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}

export default ConversionTables;